import React, { useState, useEffect } from 'react';
import {
	Calendar, Clock, ArrowRight, MapPin, ChevronRight,
	Instagram, Facebook, Twitter, Youtube, MessageCircle,
	Phone, Video, Building2, Users
} from 'lucide-react';
import { fetchData, API_URL } from '../helpers/api';
import FormatDate, { isMidnight } from '../utils/dateTools';
import '../css/home.css';
import { tarifs, tarifsNote } from '../helpers/tarifs';
import TarifsList from '../components/TarifsList';
import { newContact } from '../helpers/newsletter';
import TruncatableHtmlContent from '../utils/TruncatableHtmlContent';

const MACTeHomepage = () => {
	const [activeTab, setActiveTab] = useState('expositions');
	const [settings, setSettings] = useState(null);
	const [heroVideo, setHeroVideo] = useState(null);
	const [loading, setLoading] = useState(true);
	const [events, setEvents] = useState([]);
	const [patrimoineActualites, setPatrimoineActualites] = useState([]);
	const [email, setEmail] = useState('');
	const [formLoading, setFormLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');

	useEffect(() => {
		fetchHomeData();
	}, []);

	const fetchHomeData = async () => {
		try {
			setLoading(true);
			const promises = [
				fetchData('/api/setting?populate=*'),
				fetchData('/api/events?populate=*&filters[isPublished][$eq]=true'),
				fetchData('/api/patrimoine-actualites?populate=*')
			]
			const [settingsData, EventsData, patrimoineActualiteData] = await Promise.all(promises);

			// Sort events by start date
			const sortedEvents = EventsData.sort((a, b) => {
				return new Date(b.newStartDate) - new Date(a.newStartDate);
			});

			// Take only the first 3 events
			const limitedEvents = sortedEvents.slice(0, 3);

			// Sort patrimoineActualite articles by publication date
			const sortedArticles = patrimoineActualiteData.sort((a, b) => {
				return new Date(b.publishedOn) - new Date(a.publishedOn);
			});
			const limitedArticles = sortedArticles.slice(0, 3);

			setSettings(settingsData);
			setHeroVideo(`${API_URL}${settingsData.heroVideo.url}`);
			setEvents(limitedEvents);
			setPatrimoineActualites(limitedArticles);
		} catch (e) {
			console.error('Failed fetching data: ', e);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setFormLoading(true);
		setMessage('');
		setError('');

		try {
			const response = await newContact(email);

			console.log(response);

			if (response.success) {
				setMessage('Merci ! Vous avez correctement été inscrit à la newsletter.');
				setEmail('');
			} else {
				setError('Une erreur est survenue, merci de réessayer.');
			}
		} catch (e) {
			console.error('Error during subscription:', e);
			setError('Un erreur est survenue, merci de réessayer.');
		} finally {
			setFormLoading(false);
		}
	};

	if (loading) {
		return null;
	}

	return (
		<div className="home">
			{/* Hero Section with Video Background - More dramatic and impactful */}
			<div className="home-hero position-relative vh-100 w-100 overflow-hidden pt-5">
				{/* This would be your video, replacing with a placeholder for now */}
				{/* <div className="position-absolute top-0 start-0 end-0 bottom-0 bg-dark opacity-40 z-index-1"></div> */}
				<video
					autoPlay
					muted
					loop
					playsInline
					className="position-absolute top-0 start-0 w-100 h-100 object-fit-cover"
					style={{ zIndex: 0 }} // Explicitly set lower z-index
				>
					<source src={heroVideo} type="video/mp4" />
					{/* <source src="https://memorial-acte.fr/wp-content/uploads/2022/09/MACTe 1080.m4v" type="video/mp4" /> */}
					{/* Fallback image if video doesn't load */}
					<img
						src="https://placehold.co/1920x1080"
						alt="Mémorial ACTe"
						className="position-absolute top-0 start-0 w-100 h-100 object-fit-cover"
					/>
				</video>

				{/* Overlay with higher opacity */}
				<div className="position-absolute top-0 start-0 end-0 bottom-0 bg-dark z-index-1"></div>

				{/* Hero Content - More modern typography and layout */}
				<div className="position-absolute top-0 start-0 end-0 bottom-0 d-flex flex-column justify-content-center align-items-center text-white z-index-2 px-4 text-center">
					<h1 className="display-4 fw-bold mb-4 lh-sm" style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.3)" }}>
						CENTRE CARIBÉEN<br />
						<span className="text-warning">D'EXPRESSIONS ET DE MÉMOIRE</span><br />
						DE LA TRAITE ET DE L'ESCLAVAGE
					</h1>

					<p className="fs-4 mx-auto mb-4 fw-light" style={{ maxWidth: "800px" }}>
						Un lieu de mémoire, d'art et de culture au cœur de la Caraïbe
					</p>

					<div className="d-flex flex-column flex-sm-row gap-3">
						<a
							href={settings?.defaultBookingUrl || '#'}
							target="_blank"
							className="btn btn-warning btn-lg rounded-pill fw-semibold px-4 d-inline-flex align-items-center"
						>
							Réservez une visite
							<ChevronRight size={20} className="ms-2" />
						</a>

						<a
							href="/evenements"
							className="btn btn-outline-light btn-lg rounded-pill fw-semibold px-4 d-inline-flex align-items-center"
						>
							Agenda culturel
							<ChevronRight size={20} className="ms-2" />
						</a>
					</div>
				</div>
			</div>

			{/* Quick Links Section - New Modern Section */}
			{/* <section className="py-3 bg-light">
                <div className="container">
                    <div className="row row-cols-2 row-cols-md-4 g-3">
                        <div className="col">
                            <a href="#visiter" className="d-flex align-items-center justify-content-center p-3 bg-white shadow-sm rounded text-decoration-none hover-shadow transition">
                                <Ticket size={20} className="text-warning me-2" />
                                <span className="fw-medium">Billetterie</span>
                            </a>
                        </div>
                        <div className="col">
                            <a href="#" className="d-flex align-items-center justify-content-center p-3 bg-white shadow-sm rounded text-decoration-none hover-shadow transition">
                                <Clock size={20} className="text-warning me-2" />
                                <span className="fw-medium">Horaires</span>
                            </a>
                        </div>
                        <div className="col">
                            <a href="#" className="d-flex align-items-center justify-content-center p-3 bg-white shadow-sm rounded text-decoration-none hover-shadow transition">
                                <Map size={20} className="text-warning me-2" />
                                <span className="fw-medium">Accès</span>
                            </a>
                        </div>
                        <div className="col">
                            <a href="#events" className="d-flex align-items-center justify-content-center p-3 bg-white shadow-sm rounded text-decoration-none hover-shadow transition">
                                <Calendar size={20} className="text-warning me-2" />
                                <span className="fw-medium">Agenda</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section> */}

			{/* Visiter le MACTe Section - New Section */}
			<section id="visiter" className="py-5 bg-white">
				<div className="container py-3">
					<div className="row gx-5">
						<div className="col-lg-6 mb-5 mb-lg-0">
							<h2 className="display-5 fw-bold mb-4">VISITER LE <span className="text-warning">MACTe</span></h2>
							<p className="fs-5 text-muted mb-4">
								Le Mémorial ACTe vous accueille pour une immersion unique dans l’histoire de la traite négrière, de l’esclavage et de leurs abolitions. Découvrez l’exposition permanente et les expositions temporaires en toute autonomie.
							</p>
							<div className="bg-light p-4 rounded border-start border-warning border-4 hover-shadow transition">
								<h3 className="fs-4 fw-semibold mb-3">Tarification</h3>
								<TarifsList tarifs={tarifs} note={tarifsNote} />
							</div>
							<div className="mt-4">
								<a href={settings?.defaultBookingUrl || `#`} target="_blank" className="btn btn-dark btn-lg rounded-pill px-4 d-inline-flex align-items-center">
									Réserver ma visite
									<ChevronRight size={20} className="ms-2" />
								</a>
							</div>
						</div>
						<div className="col-lg-6">
							<div className="position-sticky" style={{ top: "100px" }}>
								<div className="position-relative rounded overflow-hidden shadow">
									<img
										src="/assets/HMP_9045-Panorama.jpg"
										alt="Visite du MACTe"
										className="w-100 object-fit-cover"
										style={{ height: 600 }}
									/>
									<div className="position-absolute bottom-0 start-0 end-0 bg-gradient-dark-transparent">
										<div className="p-4">
											<div className="bg-white bg-opacity-10 backdrop-blur rounded p-4 text-white">
												<h3 className="fs-4 fw-bold mb-3">Infos pratiques</h3>
												<div className="d-flex flex-column gap-3">
													<div className="d-flex">
														<Clock size={20} className="me-3 mt-1 text-warning" />
														<div>
															<p className="fw-semibold mb-0">Horaires d'ouverture</p>
															<p className="mb-0">Du mardi au dimanche de 9h à 18h</p>
															<p className="small text-light">Fermé le lundi</p>
														</div>
													</div>
													<div className="d-flex">
														<MapPin size={20} className="me-3 mt-1 text-warning" />
														<div>
															<p className="fw-semibold mb-0">Adresse</p>
															<p className="mb-0">Rue Raspail, 97110 Pointe-à-Pitre</p>
														</div>
													</div>
													<div className="d-flex">
														<Phone size={20} className="me-3 mt-1 text-warning" />
														<div>
															<p className="fw-semibold mb-0">Infos & réservations</p>
															<p className="mb-0">+590 590 25 16 00</p>
															<p className="mb-0">publics@macte.fr</p>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Locations d'espaces Section - Compact Banner */}
			<section className="py-4 bg-warning bg-opacity-10 border-top border-bottom border-warning border-opacity-25">
				<div className="container">
					<div className="row align-items-center justify-content-between">
						<div className="col-lg-8 mb-3 mb-lg-0">
							<div className="d-flex align-items-center">
								<div className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '48px', height: '48px', minWidth: '48px' }}>
									<Building2 size={24} className="text-white" />
								</div>
								<div>
									<h3 className="fs-4 fw-bold mb-1">Louez nos espaces pour vos événements</h3>
									<p className="mb-0 text-muted">Espaces modulables et accompagnement personnalisé pour vos événements professionnels et privés</p>
								</div>
							</div>
						</div>
						<div className="col-lg-auto">
							<a href="https://mariloo.com/etablissement/macte?onglet=espaces" target="_blank" className="btn btn-dark rounded-pill px-4 d-inline-flex align-items-center">
								Réserver en ligne
								<ChevronRight size={18} className="ms-2" />
							</a>
						</div>
					</div>
				</div>
			</section>

			{/* En Ce Moment Section - More modern and interactive */}
			<section id="events" className="py-5 bg-light">
				<div className="container py-3">
					<h2 className="display-5 fw-bold mb-2">EN CE MOMENT</h2>
					<p className="fs-4 text-muted mb-5">Découvrez les expositions et événements qui rythment la vie du MACTe</p>

					<div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
						{events.map(event => (
							<div key={event.id} className="col">
								<div className="card border-0 shadow h-100 hover-translate-up transition">
									<div className="position-relative">
										{event?.images && (
											<a href={`/evenement/${event?.documentId}`}>
												<img src={API_URL + event?.images[0]?.url} alt={event?.title || ""} />
											</a>
										)}
										{event.isFree && (
											<div className="position-absolute top-0 end-0 m-3">
												<span className="badge bg-success rounded-pill px-3 py-2">
													Gratuit
												</span>
											</div>
										)}
									</div>

									<div className="card-body d-flex flex-column">
										<div className="d-flex align-items-center text-muted mb-3 small">
											<Calendar size={16} className="me-2" />
											{event.newEndDate ? (
												<span>
													du <FormatDate dateString={event.newStartDate} format="short" />
													{" "}au{" "}
													<FormatDate dateString={event.newEndDate} format="short" />
												</span>
											) : (
												<span>
													<FormatDate dateString={event.newStartDate} format="long" />
												</span>
											)}
											{!event.newEndDate && !isMidnight(event.newStartDate) && (
												<>
													<div className="mx-2 rounded-circle bg-secondary" style={{ height: "4px", width: "4px" }}></div>
													<Clock size={16} className="me-1" />
													<span><FormatDate dateString={event.newStartDate} format="time" /></span>
												</>
											)}
										</div>

										<h3 className="fs-5 fw-bold mb-3">
											<a href={`/evenement/${event?.documentId}`} className="text-dark text-decoration-none">
												{event.title}
											</a>
										</h3>
										<div className="text-muted mb-4 flex-grow-1">
											<TruncatableHtmlContent
												html={event?.description}
												truncate={true}
												maxLength={120}
											/>
										</div>

										<a href={`/evenement/${event?.documentId}`} className="text-dark fw-medium d-inline-flex align-items-center text-decoration-none">
											Détails et réservation
											<ArrowRight size={16} className="ms-1 transition-transform event-arrow" />
										</a>
									</div>
								</div>
							</div>
						))}
					</div>

					<div className="text-center mt-5">
						<a
							href="/evenements"
							className="btn btn-dark rounded-pill px-4 py-2 d-inline-flex align-items-center"
						>
							Voir tout l'agenda
							<ArrowRight size={18} className="ms-2" />
						</a>
					</div>
				</div>
			</section>

			{/* Project d'Orientation Section - More visually appealing */}
			{/* <section className="py-5 bg-white">
				<div className="container py-3">
					<div className="row align-items-center gx-5">
						<div className="col-lg-6 mb-4 mb-lg-0">
							<img
								src="https://placehold.co/600x500"
								alt="Projet d'orientation"
								className="img-fluid rounded shadow"
							/>
						</div>

						<div className="col-lg-6">
							<h2 className="display-5 fw-bold mb-4">PROJET D'ORIENTATION</h2>

							<div className="fs-5 text-muted">
								<p>
									Le MACTe est une institution culturelle dédiée à l'histoire, au patrimoine et à la
									mémoire de la traite négrière, de l'esclavage et de leurs abolitions, inaugurée en
									mai 2015. Ce projet a été initié par la Région Guadeloupe dès 2004 suite à une
									volonté prépondérante ancrée dans la société guadeloupéenne depuis plus de trente ans.
								</p>

								<p>
									Le projet d'orientation du MACTe est un laboratoire d'innovations, un processus
									d'exploration de la médiation, qui cherche à mettre l'accent sur la dimension mémorielle
									du patrimoine et de ses expressions culturelles dans l'espace caribéen.
								</p>

								<p>
									Ce dispositif permet d'aborder des expériences muséologiques liées à la mémoire et à
									l'histoire de la traite négrière et de l'esclavage et de lier pratiques artistiques
									et culturelles contemporaines issues de la créolisation.
								</p>
							</div>

							<div className="mt-4">
								<a href="#" className="text-warning fs-5 fw-medium d-inline-flex align-items-center text-decoration-none border-bottom border-warning pb-1">
									En savoir plus sur notre projet
									<ArrowRight size={18} className="ms-2" />
								</a>
							</div>
						</div>
					</div>
				</div>
			</section> */}

			{/* Collections Showcase - New Section */}
			{/* <section className="py-5 bg-dark text-white">
                <div className="container py-3">
                    <h2 className="display-5 fw-bold mb-2">NOS COLLECTIONS</h2>
                    <p className="fs-4 text-light mb-5">Découvrez les trésors qui témoignent de l'histoire caribéenne</p>

                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                        <div className="col">
                            <div className="position-relative overflow-hidden rounded shadow-lg h-100" style={{ minHeight: "320px" }}>
                                <img
                                    src="https://placehold.co/400x500"
                                    alt="Collection permanente"
                                    className="w-100 h-100 object-fit-cover transition-transform"
                                    style={{ transform: "scale(1)", transition: "transform 0.7s ease" }}
                                    onMouseOver={(e) => e.target.style.transform = "scale(1.1)"}
                                    onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                />
                                <div className="position-absolute top-0 start-0 end-0 bottom-0 bg-gradient-transparent-dark"></div>
                                <div className="position-absolute bottom-0 start-0 end-0 p-4">
                                    <h3 className="fs-3 fw-bold mb-2">Collection permanente</h3>
                                    <p className="text-light mb-3">Parcours chronothématique sur l'esclavage et ses abolitions</p>
                                    <a href="#" className="d-inline-flex align-items-center text-warning fw-medium text-decoration-none">
                                        Explorer
                                        <ArrowRight size={16} className="ms-1 transition-transform collection-arrow" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="col">
                            <div className="position-relative overflow-hidden rounded shadow-lg h-100" style={{ minHeight: "320px" }}>
                                <img
                                    src="https://placehold.co/400x500"
                                    alt="Expositions temporaires"
                                    className="w-100 h-100 object-fit-cover transition-transform"
                                    style={{ transform: "scale(1)", transition: "transform 0.7s ease" }}
                                    onMouseOver={(e) => e.target.style.transform = "scale(1.1)"}
                                    onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                />
                                <div className="position-absolute top-0 start-0 end-0 bottom-0 bg-gradient-transparent-dark"></div>
                                <div className="position-absolute bottom-0 start-0 end-0 p-4">
                                    <h3 className="fs-3 fw-bold mb-2">Expositions temporaires</h3>
                                    <p className="text-light mb-3">Regards contemporains sur l'héritage de l'esclavage</p>
                                    <a href="#" className="d-inline-flex align-items-center text-warning fw-medium text-decoration-none">
                                        Découvrir
                                        <ArrowRight size={16} className="ms-1 transition-transform collection-arrow" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="col">
                            <div className="position-relative overflow-hidden rounded shadow-lg h-100" style={{ minHeight: "320px" }}>
                                <img
                                    src="https://placehold.co/400x500"
                                    alt="Archives et documents"
                                    className="w-100 h-100 object-fit-cover transition-transform"
                                    style={{ transform: "scale(1)", transition: "transform 0.7s ease" }}
                                    onMouseOver={(e) => e.target.style.transform = "scale(1.1)"}
                                    onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                />
                                <div className="position-absolute top-0 start-0 end-0 bottom-0 bg-gradient-transparent-dark"></div>
                                <div className="position-absolute bottom-0 start-0 end-0 p-4">
                                    <h3 className="fs-3 fw-bold mb-2">Archives et documents</h3>
                                    <p className="text-light mb-3">Témoignages historiques et ressources documentaires</p>
                                    <a href="#" className="d-inline-flex align-items-center text-warning fw-medium text-decoration-none">
                                        Consulter
                                        <ArrowRight size={16} className="ms-1 transition-transform collection-arrow" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section> */}

			{/* Testimonials Section - New Section */}
			{/* <section className="py-5 bg-light">
                <div className="container py-3 text-center">
                    <h2 className="display-5 fw-bold mb-2">TÉMOIGNAGES</h2>
                    <p className="fs-4 text-muted mb-5 mx-auto" style={{ maxWidth: "700px" }}>Ils ont visité le MACTe</p>

                    <div className="row row-cols-1 row-cols-md-3 g-4">
                        <div className="col">
                            <div className="card h-100 border-0 shadow-sm p-4">
                                <div className="d-flex justify-content-center mb-3">
                                    <div className="rounded-circle bg-warning bg-opacity-25 d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px" }}>
                                        <span className="text-warning fs-3 fw-bold">M</span>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <p className="text-muted mb-4 fst-italic">
                                        "Un lieu de mémoire essentiel pour comprendre l'histoire des Caraïbes. La médiation culturelle est excellente et les collections sont riches."
                                    </p>
                                    <p className="fw-semibold mb-0">Jean-Marc P.</p>
                                    <p className="small text-muted">Fort-de-France, Martinique</p>
                                </div>
                            </div>
                        </div>

                        <div className="col">
                            <div className="card h-100 border-0 shadow-sm p-4">
                                <div className="d-flex justify-content-center mb-3">
                                    <div className="rounded-circle bg-warning bg-opacity-25 d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px" }}>
                                        <span className="text-warning fs-3 fw-bold">S</span>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <p className="text-muted mb-4 fst-italic">
                                        "J'ai adoré l'exposition temporaire et les événements culturels proposés. Un centre culturel vivant qui donne à réfléchir et à ressentir."
                                    </p>
                                    <p className="fw-semibold mb-0">Sophie T.</p>
                                    <p className="small text-muted">Basse-Terre, Guadeloupe</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-5">
                        <a
                            href="#"
                            className="btn btn-outline-secondary rounded-pill px-4 py-2 d-inline-flex align-items-center"
                        >
                            Voir tous les témoignages
                            <ArrowRight size={18} className="ms-2" />
                        </a>
                    </div>
                </div>
            </section> */}

			{/* Patrimoine & Actualités Section */}
			<section className="py-5 bg-light">
				<div className="container py-3">
					<h2 className="display-5 fw-bold mb-2">PATRIMOINE & <span className="text-warning">ACTUALITÉS</span></h2>
					<p className="fs-4 text-muted mb-5">Découvrez notre patrimoine et les dernières actualités</p>

					<div className="row row-cols-1 row-cols-md-3 g-4">
						{patrimoineActualites.map(article => (
							<div key={article.documentId} className="col">
								<div className="card border-0 shadow h-100 hover-translate-up transition">
									<div className="position-relative">
										{article?.images && article.images.length > 0 && (
											<a href={`/patrimoine-actualites/${article?.documentId}`}>
												<img
													src={API_URL + article?.images[0]?.url}
													alt={article?.title}
													className="w-100 object-fit-cover"
													style={{ height: '220px' }}
												/>
											</a>
										)}
										{/* Video indicator */}
										{article.video && article.video.url && (
											<div className="position-absolute top-0 end-0 m-2">
												<span className="badge bg-dark rounded-pill px-2 py-1 d-flex align-items-center">
													<Video size={14} className="me-1" />
													Vidéo
												</span>
											</div>
										)}
									</div>

									<div className="card-body d-flex flex-column">
										<div className="d-flex align-items-center text-muted mb-3 small">
											<Calendar size={16} className="me-2" />
											<span>
												<FormatDate dateString={article.publishedOn} format="long" />
											</span>
										</div>

										<h3 className="fs-5 fw-bold mb-3">
											<a href={`/patrimoine-actualites/${article?.documentId}`} className="text-dark text-decoration-none">
												{article.title}
											</a>
										</h3>
										<div className="text-muted mb-4 flex-grow-1">
											<TruncatableHtmlContent
												html={article?.content}
												truncate={true}
												maxLength={120}
											/>
										</div>

										<a href={`/patrimoine-actualites/${article?.documentId}`} className="text-dark fw-medium d-inline-flex align-items-center text-decoration-none">
											Lire la suite
											<ArrowRight size={16} className="ms-1 transition-transform event-arrow" />
										</a>
									</div>
								</div>
							</div>
						))}
					</div>

					<div className="text-center mt-5">
						<a
							href="/patrimoine-actualites"
							className="btn btn-dark rounded-pill px-4 py-2 d-inline-flex align-items-center"
						>
							Voir tous les articles
							<ArrowRight size={18} className="ms-2" />
						</a>
					</div>
				</div>
			</section>

			{/* Newsletter & Social Section - New Section */}
			<section className="py-5 bg-warning text-dark">
				<div className="container py-3">
					<div className="row g-5">
						<div className="col-lg-6">
							<h2 className="display-6 fw-bold mb-3">RESTEZ INFORMÉ</h2>
							<p className="fs-5 mb-4">
								Recevez notre newsletter mensuelle et ne manquez aucun événement du MACTe
							</p>

							<form className="row g-2 mb-3" style={{ maxWidth: "500px" }} onSubmit={handleSubmit}>
								<div className="col">
									<input
										type="email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										placeholder="Votre adresse email"
										className="form-control form-control-lg rounded-pill"
										required
									/>
								</div>
								<div className="col-auto">
									<button
										type="submit"
										className="btn btn-dark btn-lg rounded-pill px-4"
										disabled={formLoading}
									>
										{formLoading ? 'Chargement...' : 'S\'inscrire'}
									</button>
								</div>
							</form>
							{message && <p className="success-message">{message}</p>}
							{error && <p className="error-message">{error}</p>}
						</div>

						<div className="col-lg-6 ps-lg-5 border-start-lg">
							<h2 className="display-6 fw-bold mb-3">SUIVEZ-NOUS</h2>
							<p className="fs-5 mb-4">
								Rejoignez notre communauté sur les réseaux sociaux
							</p>

							<div className="d-flex flex-wrap gap-3 mb-4">
								<a href={settings?.facebook || '#'} target="_blank" className="btn btn-light text-dark p-3 rounded-circle">
									<Facebook size={24} />
								</a>
								<a href={settings?.instagram || '#'} target="_blank" className="btn btn-light text-dark p-3 rounded-circle">
									<Instagram size={24} />
								</a>
								<a href={settings?.twitter || '#'} target="_blank" className="btn btn-light text-dark p-3 rounded-circle">
									<Twitter size={24} />
								</a>
								<a href={settings?.youtube || '#'} target="_blank" className="btn btn-light text-dark p-3 rounded-circle">
									<Youtube size={24} />
								</a>
							</div>

							<div className="mt-4">
								<p className="fw-medium mb-3">
									Actualités et événements en temps réel sur WhatsApp
								</p>
								<a
									href={settings?.whatsapp || '#'}
									target="_blank"
									className="btn btn-light text-dark rounded-pill px-4 d-inline-flex align-items-center"
								>
									<MessageCircle size={20} className="me-2" />
									Rejoindre sur WhatsApp
								</a>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Virtual Tour - New Section */}
			{/* <section className="position-relative py-5 bg-dark text-white overflow-hidden">
                <div className="position-absolute top-0 start-0 w-100 h-100">
                    <img
                        src="https://placehold.co/1920x1080"
                        alt="Interior of MACTe"
                        className="w-100 h-100 object-fit-cover opacity-25"
                    />
                </div>

                <div className="container py-5 position-relative">
                    <div style={{ maxWidth: "800px" }}>
                        <h2 className="display-5 fw-bold mb-3">VISITE VIRTUELLE 360°</h2>
                        <p className="fs-4 text-light mb-4">
                            Explorez le Mémorial ACTe depuis chez vous grâce à notre visite virtuelle immersive
                        </p>

                        <div className="d-flex flex-column flex-sm-row gap-3">
                            <a
                                href="#"
                                className="btn btn-warning btn-lg rounded-pill px-4 d-inline-flex align-items-center"
                            >
                                Démarrer la visite virtuelle
                                <Play size={20} className="ms-2" />
                            </a>

                            <a
                                href="#"
                                className="btn btn-outline-light btn-lg rounded-pill px-4 d-inline-flex align-items-center"
                            >
                                En savoir plus
                                <ChevronRight size={20} className="ms-2" />
                            </a>
                        </div>
                    </div>
                </div>
            </section> */}

			{/* Contact & Location Section */}
			{/* <section className="py-5 bg-white">
                <div className="container py-3">
                    <div className="row g-5">
                        <div className="col-lg-6">
                            <h2 className="display-5 fw-bold mb-4">NOUS CONTACTER</h2>

                            <div className="d-flex flex-column gap-4 fs-5">
                                <div className="d-flex">
                                    <MapPin size={24} className="text-warning me-3 mt-1" />
                                    <div>
                                        <h3 className="fs-5 fw-semibold mb-1">Adresse</h3>
                                        <p className="mb-0">Rue Raspail</p>
                                        <p className="mb-0">97110 Pointe-à-Pitre, Guadeloupe</p>
                                    </div>
                                </div>

                                <div className="d-flex">
                                    <Clock size={24} className="text-warning me-3 mt-1" />
                                    <div>
                                        <h3 className="fs-5 fw-semibold mb-1">Horaires d'ouverture</h3>
                                        <p className="mb-0">Mardi au dimanche : 9h - 18h</p>
                                        <p className="text-muted mb-0">Fermeture de la billetterie à 17h30</p>
                                        <p className="text-muted mb-0">Fermé le lundi</p>
                                    </div>
                                </div>

                                <div className="d-flex">
                                    <Phone size={24} className="text-warning me-3 mt-1" />
                                    <div>
                                        <h3 className="fs-5 fw-semibold mb-1">Téléphone</h3>
                                        <p className="mb-0">+590 590 25 16 00</p>
                                    </div>
                                </div>

                                <div className="d-flex">
                                    <Mail size={24} className="text-warning me-3 mt-1" />
                                    <div>
                                        <h3 className="fs-5 fw-semibold mb-1">Email</h3>
                                        <p className="mb-0">contact@memorial-acte.fr</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-5">
                                <h3 className="fs-4 fw-semibold mb-3">Formulaire de contact</h3>
                                <form className="row g-3">
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            placeholder="Nom"
                                            className="form-control rounded-3"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="email"
                                            placeholder="Email"
                                            className="form-control rounded-3"
                                        />
                                    </div>
                                    <div className="col-12">
                                        <input
                                            type="text"
                                            placeholder="Sujet"
                                            className="form-control rounded-3"
                                        />
                                    </div>
                                    <div className="col-12">
                                        <textarea
                                            placeholder="Message"
                                            rows="4"
                                            className="form-control rounded-3"
                                        ></textarea>
                                    </div>
                                    <div className="col-12">
                                        <button
                                            type="submit"
                                            className="btn btn-dark rounded-pill px-4 py-2"
                                        >
                                            Envoyer
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <div className="h-100 rounded overflow-hidden shadow-lg" style={{ minHeight: "600px" }}>
                                <img
                                    src="https://placehold.co/800x600"
                                    alt="Carte du Memorial ACTe"
                                    className="w-100 h-100 object-fit-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section> */}
		</div>
	);
};

export default MACTeHomepage;